import { useMemo } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { formatVolume, MUSCLE_LABEL } from "@jackedlogs/shared";

import { PanelTitleRow } from "./InfoPill";
import { ErrorCard, LoadingCard } from "./panelCards";
import { makeTierOneStyles } from "./panelStyles";
import { DEFAULT_WINDOW_DAYS } from "./progressWindow";
import { type PainVolumeRow, computePainVolume } from "./tierOneMath";
import type { ProgressDeps } from "./types";
import { useSessionResultsFromDeps } from "./useSessionResults";

// Per-muscle pain × recent volume snapshot. Text-first by intent — "is the
// painful muscle taking heavy load?" at a glance.
export function PainVolumePanel(deps: ProgressDeps) {
  const { theme, units, painMap } = deps;
  const windowDays = deps.windowDays ?? DEFAULT_WINDOW_DAYS;
  const { t, i18n } = useTranslation();
  const { results, error } = useSessionResultsFromDeps(deps);
  const styles = useMemo(() => makeTierOneStyles(theme), [theme]);

  const rows = useMemo<PainVolumeRow[] | null>(() => {
    if (!results) return null;
    return computePainVolume(results, painMap, Date.now(), windowDays);
  }, [results, painMap, windowDays]);

  const painTitle = t("mobile.progress.pain.title", {
    defaultValue: "Pain × volume",
  });
  const painHelp = t("mobile.progress.pain.help", {
    days: windowDays,
    defaultValue:
      "For every muscle currently flagged in your painMap, shows recent training load. Red dot = pain reported red (avoid heavy load), yellow = caution, green = monitor. The two volume numbers are last-7-day vs the selected {{days}}-day load on that muscle — sustained heavy load on a red muscle is the highest-risk signal in the dashboard.",
  });
  const period = t("progress.period.painDays", {
    defaultValue: "7 / {{days}} d",
    days: windowDays,
  });

  if (error) return <ErrorCard theme={theme} titleKey="pain" />;
  if (rows === null) return <LoadingCard theme={theme} titleKey="pain" />;

  if (rows.length === 0) {
    return (
      <View style={styles.card}>
        <PanelTitleRow
          theme={theme}
          title={painTitle}
          body={painHelp}
          period={period}
        />
        <Text style={styles.empty}>
          {t("mobile.progress.pain.noPain", {
            defaultValue: "No muscles flagged in painMap",
          })}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <PanelTitleRow
        theme={theme}
        title={painTitle}
        body={painHelp}
        period={period}
      />
      {rows.map((row) => {
        // row.muscleSlug is the bodymap MUSCLE_SLUGS vocabulary (distinct from
        // the catalog vocab muscleKey() takes). Localized via a dedicated
        // bodymap key set; MUSCLE_LABEL is the EN fallback.
        const label = t(`mobile.bodymap.muscle.${row.muscleSlug}`, {
          defaultValue: MUSCLE_LABEL[row.muscleSlug] ?? row.muscleSlug,
        });
        // Severity colour: red = danger, yellow = warn (status, not brand),
        // green = neutral ink. Caution off the brand per colour budget S2.4.
        const sevColor =
          row.severity === "red"
            ? theme.palette.danger
            : row.severity === "yellow"
              ? theme.palette.warn
              : theme.palette.ink2;
        // Severity as a WORD next to the dot — colour alone fails the
        // never-color-only rule ([[feedback_user_visual_accessibility]]).
        const sevWord =
          row.severity === "red"
            ? t("mobile.progress.pain.sevRed", { defaultValue: "Avoid" })
            : row.severity === "yellow"
              ? t("mobile.progress.pain.sevYellow", { defaultValue: "Caution" })
              : t("mobile.progress.pain.sevGreen", { defaultValue: "Monitor" });
        return (
          <View key={row.muscleSlug} style={styles.painRow}>
            <View style={styles.painLabelRow}>
              <View style={[styles.painDot, { backgroundColor: sevColor }]} />
              <Text style={[styles.painSeverity, { color: sevColor }]}>
                {sevWord}
              </Text>
              <Text style={styles.balanceLabel} numberOfLines={1}>
                {label}
              </Text>
              <Text style={styles.balanceValue}>
                {formatVolume(row.volume7dKg, units, i18n.language)}
                {" / 7d · "}
                {formatVolume(row.volumeWindowKg, units, i18n.language)}
                {` / ${windowDays}d`}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
}
